import React, { useRef, useState } from "react";
import type { VisitQuery, VisitsAccess } from "../shared/visits.ts";
import { Button, Field, Modal } from "./ui.tsx";
import {
  constrainVisitSubgroup,
  constrainVisitTab,
} from "./visits-view-model.ts";
import "./visits-filters.css";

const initialQuery: VisitQuery = { page: 1, pageSize: 25 };
const statusNames = {
  visited: "Visitado",
  pending: "Pendente",
  analysis: "Aguardando análise",
};
type Choice = { id: string; name: string };
type Props = {
  value: VisitQuery;
  access: VisitsAccess;
  onApply: (query: VisitQuery) => void;
  onClose: () => void;
};
const hasValue = (value: unknown) => value !== undefined && value !== "";
const count = (query: VisitQuery, keys: Array<keyof VisitQuery>) =>
  keys.filter((key) => hasValue(query[key])).length;

/** Local form state only. Closing, canceling or clearing does not submit a query. */
export function VisitsFilters({ value, access, onApply, onClose }: Props) {
  const constrain = (
    query: VisitQuery,
    previousSourceId = query.sourceId,
    previousGroupId = query.groupId,
  ): VisitQuery => {
    const next = constrainVisitTab(query, access.sources, previousSourceId);
    // Existing global "without subgroup" queries are valid in the server contract.
    // Preserve them until an explicit parent change, without inventing catalog entries.
    if (
      next.subgroupId === "__unmapped__" &&
      !next.groupId &&
      previousGroupId === next.groupId
    )
      return next;
    return constrainVisitSubgroup(next, access.groups, previousGroupId);
  };
  const [draft, setDraft] = useState<VisitQuery>(() => constrain(value));
  const formRef = useRef<HTMLFormElement>(null);
  const set = (key: keyof VisitQuery, next: string) =>
    setDraft((previous) =>
      constrain(
        {
          ...previous,
          [key]: next || undefined,
          ...(key === "status" ? { metricStatus: undefined } : {}),
        },
        previous.sourceId,
        previous.groupId,
      ),
    );
  const select = (
    label: string,
    key: keyof VisitQuery,
    options: Choice[],
    settings: { empty?: string; unmapped?: string; always?: boolean } = {},
  ) => {
    const selected = String(draft[key] || "");
    const show = settings.always || options.length > 0 || !!selected;
    if (!show) return null;
    const unmapped = settings.unmapped && selected === "__unmapped__";
    return (
      <Field label={label}>
        <select
          value={selected}
          onChange={(event) => set(key, event.target.value)}
        >
          <option value="">{settings.empty || "Todos"}</option>
          {unmapped && (
            <option value="__unmapped__">{settings.unmapped}</option>
          )}
          {selected &&
            selected !== "__unmapped__" &&
            !options.some((option) => option.id === selected) && (
              <option value={selected}>Seleção anterior indisponível</option>
            )}
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      </Field>
    );
  };
  const tabs = access.sources
    .filter((source) => !!draft.sourceId && source.id === draft.sourceId)
    .flatMap((source) =>
      source.tabs.map((tab) => ({
        id: tab.id,
        name: `${source.name} · ${tab.title}`,
      })),
    );
  const groups = access.groups.filter((group) => !group.parentId);
  const subgroups = access.groups.filter(
    (group) => !!group.parentId && group.parentId === draft.groupId,
  );
  const responsible = Object.entries(access.responsibleNames).map(
    ([id, name]) => ({ id, name }),
  );
  const authors = Object.entries(access.authorNames).map(([id, name]) => ({
    id,
    name,
  }));
  const personKeys: Array<keyof VisitQuery> = [
    "responsibleId",
    "visitAuthorId",
    ...(access.permissions.personal ? ["resident" as const] : []),
  ];
  const active = Object.entries(draft).filter(
    ([key, item]) => !["page", "pageSize"].includes(key) && hasValue(item),
  ).length;
  const activeLabel = (number: number) => (
    <span className="vo-filter-count">
      {number} {number === 1 ? "ativo" : "ativos"}
    </span>
  );
  const revealField = (target: EventTarget | null) => {
    if (
      !(target instanceof HTMLElement) ||
      !target.matches("input,select,textarea")
    )
      return;
    let parent = target.parentElement;
    while (parent && parent !== formRef.current) {
      if (parent instanceof HTMLDetailsElement) parent.open = true;
      parent = parent.parentElement;
    }
  };
  return (
    <Modal
      title="Filtrar visitas"
      onClose={onClose}
      wide
      mobileFullScreen
      className="vo-filter-dialog"
    >
      <form
        ref={formRef}
        className="vo-filter-layout"
        noValidate
        onInvalidCapture={(event) => revealField(event.target)}
        onFocusCapture={(event) => revealField(event.target)}
        onSubmit={(event) => {
          event.preventDefault();
          const form = event.currentTarget;
          // Open hidden sections before asking the browser to focus invalid controls.
          if (!form.checkValidity()) {
            form.querySelectorAll(":invalid").forEach(revealField);
            form.reportValidity();
            return;
          }
          onApply(constrain({ ...draft, page: 1 }));
        }}
      >
        <div className="vo-filter-body">
          <p className="vo-filter-summary" role="status" aria-live="polite">
            {active === 0
              ? "Nenhum filtro selecionado"
              : `${active} ${active === 1 ? "filtro selecionado" : "filtros selecionados"}`}
          </p>
          <fieldset className="vo-filter-primary">
            <legend>
              Filtros principais
              {activeLabel(
                count(draft, ["sourceId", "groupId"]) +
                  (draft.status || draft.metricStatus ? 1 : 0),
              )}
            </legend>
            <Field label="Situação">
              <select
                value={draft.status ?? draft.metricStatus ?? ""}
                onChange={(event) => set("status", event.target.value)}
              >
                <option value="">Todas</option>
                {Object.entries(statusNames).map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </Field>
            {draft.status &&
              draft.metricStatus &&
              draft.status !== draft.metricStatus && (
                <p className="vo-filter-help" role="status">
                  Há dois recortes de situação diferentes nesta consulta.
                  Escolha uma situação para substituir os dois.
                </p>
              )}
            {select("Planilha de origem", "sourceId", access.sources, {
              empty: "Todas as planilhas",
              always: true,
            })}
            {select("Grupo", "groupId", groups, {
              empty: "Todos os grupos",
              unmapped: "Sem grupo",
              always: true,
            })}
          </fieldset>
          <details className="vo-filter-section">
            <summary>
              <span>Localização</span>
              {activeLabel(
                count(draft, [
                  "street",
                  "number",
                  "neighborhood",
                  "subgroupId",
                ]),
              )}
            </summary>
            <div className="vo-filter-section-fields">
              <Field label="Rua">
                <input
                  value={draft.street || ""}
                  onChange={(event) => set("street", event.target.value)}
                  autoComplete="off"
                />
              </Field>
              <Field label="Número">
                <input
                  value={draft.number || ""}
                  onChange={(event) => set("number", event.target.value)}
                  inputMode="numeric"
                />
              </Field>
              <Field label="Bairro">
                <input
                  value={draft.neighborhood || ""}
                  onChange={(event) => set("neighborhood", event.target.value)}
                />
              </Field>
              {select("Subgrupo", "subgroupId", subgroups, {
                unmapped: "Sem subgrupo",
              })}
              {!draft.groupId &&
                !draft.subgroupId &&
                access.groups.some((group) => !!group.parentId) && (
                  <p className="vo-filter-help">
                    Escolha um grupo para filtrar por subgrupo.
                  </p>
                )}
            </div>
          </details>
          {(access.permissions.personal ||
            responsible.length > 0 ||
            authors.length > 0 ||
            draft.responsibleId ||
            draft.visitAuthorId) && (
            <details className="vo-filter-section">
              <summary>
                <span>Pessoas</span>
                {activeLabel(count(draft, personKeys))}
              </summary>
              <div className="vo-filter-section-fields">
                {select("Responsável atual", "responsibleId", responsible, {
                  unmapped: "Sem responsável confirmado",
                })}
                {access.permissions.personal && (
                  <Field label="Nome do morador">
                    <input
                      value={draft.resident || ""}
                      onChange={(event) => set("resident", event.target.value)}
                      autoComplete="off"
                    />
                  </Field>
                )}
                {select("Autor da visita", "visitAuthorId", authors, {
                  unmapped: "Sem autor confirmado",
                })}
              </div>
            </details>
          )}
          <details className="vo-filter-section">
            <summary>
              <span>Período e data</span>
              {activeLabel(count(draft, ["dateFrom", "dateTo", "dateType"]))}
            </summary>
            <div className="vo-filter-section-fields">
              <Field label="Data utilizada">
                <select
                  value={draft.dateType || ""}
                  required={!!draft.dateFrom || !!draft.dateTo}
                  onChange={(event) => set("dateType", event.target.value)}
                >
                  <option value="">Selecione</option>
                  <option value="first_visit">Primeira visita conhecida</option>
                  <option value="latest_visit">Última visita conhecida</option>
                </select>
              </Field>
              <Field label="De">
                <input
                  type="date"
                  value={draft.dateFrom || ""}
                  onChange={(event) => set("dateFrom", event.target.value)}
                />
              </Field>
              <Field label="Até">
                <input
                  type="date"
                  value={draft.dateTo || ""}
                  onChange={(event) => set("dateTo", event.target.value)}
                  min={draft.dateFrom}
                />
              </Field>
              <p className="vo-filter-help">
                A última visita não comprova a primeira. Datas desconhecidas não
                são reconstruídas.
              </p>
            </div>
          </details>
          <details className="vo-filter-section">
            <summary>
              <span>Origem detalhada</span>
              {activeLabel(count(draft, ["tabId"]))}
            </summary>
            <div className="vo-filter-section-fields">
              {draft.sourceId ? (
                select("Aba de origem", "tabId", tabs, {
                  empty: "Todas as abas",
                  always: true,
                })
              ) : (
                <p className="vo-filter-help">
                  Escolha uma planilha para filtrar por aba.
                </p>
              )}
            </div>
          </details>
        </div>
        <div className="vo-filter-footer">
          <Button type="button" onClick={() => setDraft(initialQuery)}>
            Limpar
          </Button>
          <Button type="submit" kind="primary">
            Aplicar filtros
          </Button>
        </div>
      </form>
    </Modal>
  );
}
